import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import './App.css';

const Login = lazy(() => import('./Login'));
const ChatRoom = lazy(() => import('./ChatRoom'));

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [theme, setTheme] = useState('light');
  const [encryptionKey, setEncryptionKey] = useState('');
  const [killswitchEnabled, setKillswitchEnabled] = useState(false);
  const [cacheCredentials, setCacheCredentials] = useState(() => {
    return localStorage.getItem('cache_settings_enabled') === 'true';
  });
  const [loginHistory, setLoginHistory] = useState([]);
  const [initialOnlineUsers, setInitialOnlineUsers] = useState([]);
  const [initialOnlineDevices, setInitialOnlineDevices] = useState({});
  const [initialProfile, setInitialProfile] = useState(null);

  useEffect(() => {
    localStorage.setItem('cache_settings_enabled', cacheCredentials);
    if (!cacheCredentials) {
      localStorage.removeItem('cached_creds');
    }
  }, [cacheCredentials]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const setAppHeight = () => {
      const viewport = window.visualViewport;
      const viewportHeight = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${Math.round(viewportHeight)}px`);
    };

    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setAppHeight);
    }

    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setAppHeight);
      }
    };
  }, []);

  useEffect(() => {
    const handleBlur = () => {
      if (killswitchEnabled) {
        handleKill();
      }
    };

    const handleKeyDown = (e) => {
      if (killswitchEnabled && e.key === 'Shift') {
        handleKill();
      }
    };

    const handlePopState = (e) => {
      if (killswitchEnabled) {
        handleKill();
      } else {
        // If killswitch is off, just push state back to prevent navigation
        window.history.pushState(null, null, window.location.href);
      }
    };

    // Push initial state to trap the back button
    window.history.pushState(null, null, window.location.href);

    window.addEventListener('blur', handleBlur);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [killswitchEnabled]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const toggleKillswitch = () => {
    setKillswitchEnabled(prev => !prev);
  };

  const toggleCacheCredentials = () => {
    setCacheCredentials(prev => !prev);
  };

  const handleLogin = (username, userToken, history, onlineUsers, onlineDevices, profile) => {
    setUser(username);
    setToken(userToken);
    setLoginHistory(history || []);
    setInitialOnlineUsers(onlineUsers || []);
    setInitialOnlineDevices(onlineDevices || {});
    setInitialProfile(profile || null);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setEncryptionKey('');
    setInitialProfile(null);
  };

  const handleKill = () => {
    // 1. Overwrite sensitive data with noise before discarding
    setEncryptionKey('VOID_' + Math.random().toString(36));
    setToken('VOID_' + Math.random().toString(36));
    
    // 2. Unmount everything to drop references to message arrays
    setUser(null);
    setKillswitchEnabled(false);
    setInitialProfile(null);

    // 3. Clear all possible browser persistence
    window.localStorage.clear();
    window.sessionStorage.clear();

    // 4. Attempt to close the tab using the "self-open" trick
    // This is the most effective way to bypass browser security blocks on window.close()
    try {
      window.open('', '_self', '');
      window.close();
    } catch (e) {
      console.error("Close failed", e);
    }

    // 5. Fallback: If the tab is still open, redirect to a blank page immediately
    setTimeout(() => {
      window.location.replace('about:blank');
    }, 5);
  };

  return (
    <Router>
      <div className={`App ${!killswitchEnabled ? 'safety-off' : ''}`}>
        {!user && (
          <div className="top-controls">
            <button 
              onClick={toggleTheme} 
              className="theme-toggle"
              title="Toggle Theme"
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
          </div>
        )}
        <main className="main-content">
          <Suspense fallback={<div className="loading-screen">Loading...</div>}>
            <Routes>
              <Route 
                path="/login" 
                element={!user ? <Login onLogin={handleLogin} encryptionKey={encryptionKey} setEncryptionKey={setEncryptionKey} cacheCredentials={cacheCredentials} /> : <Navigate to="/c" />} 
              />
              <Route 
                path="/c" 
                element={
                  user ? (
                    <ChatRoom 
                      user={user}
                      token={token}
                      onLogout={handleLogout} 
                      theme={theme} 
                      toggleTheme={toggleTheme} 
                      encryptionKey={encryptionKey}
                      cacheCredentials={cacheCredentials}
                      toggleCacheCredentials={toggleCacheCredentials}
                      killswitchEnabled={killswitchEnabled}
                      toggleKillswitch={toggleKillswitch}
                      loginHistory={loginHistory}
                      initialOnlineUsers={initialOnlineUsers}
                      initialOnlineDevices={initialOnlineDevices}
                      initialProfile={initialProfile}
                    />
                  ) : (
                    <Navigate to="/login" />
                  )
                } 
              />
              <Route 
                path="/" 
                element={<Navigate to={user ? "/c" : "/login"} />} 
              />
            </Routes>
          </Suspense>
        </main>
      </div>
    </Router>
  );
}

export default App;
